import Vue from 'vue'
import orgIntegrationApiKeyService from '@/services/orgIntegrationApiKeyService'

const STORAGE_KEY_PREFIX = 'hodo_org_api_key_status_'

function getStorageKey (orgId) {
  return `${STORAGE_KEY_PREFIX}${orgId || '0'}`
}

function normalizeProviderCode (provider) {
  if (provider === undefined || provider === null) return ''
  return String(provider).trim().toUpperCase()
}

function buildStatusState ({ orgId, providers = [] } = {}) {
  const byProviderCode = {}
  const byProviderId = {}
  const apiByProviderCode = {}
  const apiByProviderId = {}

  providers.forEach((item) => {
    const providerId = item?.id
    const providerCode = normalizeProviderCode(item?.code)
    const apiConfig = item?.api_config || null
    const hasApiKey = !!(apiConfig?.api_key && String(apiConfig.api_key).trim())

    if (providerId !== null && providerId !== undefined) {
      byProviderId[providerId] = hasApiKey
      apiByProviderId[providerId] = apiConfig
    }
    if (providerCode) {
      byProviderCode[providerCode] = hasApiKey
      apiByProviderCode[providerCode] = apiConfig
    }
  })

  return {
    orgId: orgId || null,
    providers,
    byProviderCode,
    byProviderId,
    apiByProviderCode,
    apiByProviderId,
    loadedAt: new Date().toISOString()
  }
}

function getGlobalState () {
  return Vue.prototype.$orgApiKeyStatus || null
}

function setGlobalState (state) {
  Vue.prototype.$orgApiKeyStatus = state || {}
}

function saveToStorage (state) {
  if (typeof window === 'undefined') return
  const orgId = state?.orgId
  if (!orgId) return
  window.localStorage.setItem(getStorageKey(orgId), JSON.stringify(state))
}

function readFromStorage (orgId) {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(getStorageKey(orgId))
  if (!raw) return null

  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

export function clearOrgApiKeyStatusGlobal () {
  setGlobalState({})
}

export function setOrgApiKeyStatusGlobal ({ orgId, providers = [] } = {}) {
  const state = buildStatusState({ orgId, providers })
  setGlobalState(state)
  saveToStorage(state)
  return state
}

export function hydrateOrgApiKeyStatusGlobal (orgId) {
  if (!orgId) return null
  const cached = readFromStorage(orgId)
  if (!cached) return null
  setGlobalState(cached)
  return cached
}

export async function syncOrgApiKeyStatusGlobal ({ orgId }) {
  if (!orgId) return null
  const providers = await orgIntegrationApiKeyService.getCustomersWithApiConfig({ orgId })
  return setOrgApiKeyStatusGlobal({ orgId, providers })
}

export function getApiByProvider (provider) {
  const state = getGlobalState()
  if (!state) return null

  if (typeof provider === 'number') {
    return state.apiByProviderId?.[provider] || null
  }

  const providerCode = normalizeProviderCode(provider)
  if (!providerCode) return null
  return state.apiByProviderCode?.[providerCode] || null
}

export function checkApiKeyStatus (provider) {
  const state = getGlobalState()
  if (!state) return false

  if (typeof provider === 'number') {
    return !!state.byProviderId?.[provider]
  }

  const providerCode = normalizeProviderCode(provider)
  if (!providerCode) return false
  return !!state.byProviderCode?.[providerCode]
}
